/**
 * Browser-compatible hash utility for i18n keys
 * Generates the same hash keys as the Node.js codemod script
 */

/**
 * Simple MD5-like hash function for browser
 * Uses the same algorithm as the Node.js version for consistency
 */
async function md5Hash(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    // Use first 8 chars of hex hash (same as Node version)
    return hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
        .substring(0, 8);
}

/**
 * Synchronous hash using simple djb2 algorithm
 * This provides consistent hashing without async
 */
function simpleHash(text: string, length: number = 8): string {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) {
        hash = (hash * 33) ^ text.charCodeAt(i);
    }
    // Convert to unsigned 32-bit integer and then to hex
    const hexHash = (hash >>> 0).toString(16).padStart(8, "0");
    return hexHash.substring(0, length);
}

/**
 * Generates a hash key from English text
 * Must match the Node.js codemod hash function exactly
 * @param text - The English text to hash
 * @returns Hash key prefixed with 'k_'
 */
export function generateHashKey(text: string): string {
    const normalizedText = text.trim().toLowerCase().replace(/\s+/g, " ");
    const hash = simpleHash(normalizedText);
    return `k_${hash}`;
}

/**
 * Cache for hash lookups to avoid recalculating
 */
const hashCache = new Map<string, string>();

/**
 * Get hash key with caching
 */
export function getHashKey(text: string): string {
    if (hashCache.has(text)) {
        return hashCache.get(text)!;
    }
    const hashKey = generateHashKey(text);
    hashCache.set(text, hashKey);
    return hashKey;
}

/**
 * Returns a data-i18n attribute object for spreading onto JSX elements
 * Usage: <button {...i18nKey("Search PNR")}>{t("Search PNR")}</button>
 */
export function i18nKey(text: string): { "data-i18n": string } {
    return { "data-i18n": getHashKey(text) };
}
